// src/theme/tokens.ts
// Single source of truth for the "Playful Creator" design system.
// Screens never use hex literals — they import from '@/theme'.
// Light-only today; a dark variant later means a second `colors` set
// behind a context, with these keys as the contract.
import type { TextStyle } from 'react-native';

export const colors = {
  bg: '#FFF8F1',           // warm cream canvas
  surface: '#FFFFFF',      // cards, sheets, tab bar
  text: '#1F2937',
  textMuted: '#8A7E74',    // warm gray — no cold grays anywhere
  border: '#F3E8DD',       // hairlines + card clay-shadow tone
  chevron: '#D6C7B8',
  primary: '#F97316',
  primaryPressed: '#EA580C',
  primaryDepth: '#C2410C', // clay shadow under primary surfaces
  primarySoft: '#FFEDD5',  // tints, selection, soft icon chips
  success: '#16A34A',
  successSoft: '#DCFCE7',
  successText: '#15803D',
  warning: '#F59E0B',
  danger: '#EF4444',
  dangerSoft: '#FEF2F2',
  dangerDepth: '#FECACA',
  dangerText: '#B91C1C',
} as const;

// Brand world (midnight) — marketing, onboarding splash, the in-app Liftoff moment.
// Used only as accents inside the warm app world.
export const brand = {
  midnight: '#0B0E27',
  indigo: '#1B1F4B',
  earthBlue: '#2E5BFF',
  lunarGold: '#E6B85C',
} as const;

// Glass: frosted chrome + sheets — "things that float." Clay stays for "things you touch."
export const glass = {
  fill: 'rgba(255,255,255,0.6)',
  borderTop: 'rgba(255,255,255,0.85)',
  shadow: 'rgba(194,65,12,0.12)',
  blur: 24,
  tint: 'light' as const,
} as const;

// Canvas gradient behind glass chrome (warm cream → peach); midnight for brand world.
export const gradients = {
  canvas: ['#FFF8F1', '#FFEEDD'] as const,
  midnight: ['#0B0E27', '#1B1F4B'] as const,
} as const;

export interface PastelPair {
  bg: string;
  fg: string;
}

// Identity pairs for project/app chips + avatars.
export const pastels: readonly PastelPair[] = [
  { bg: '#FDE68A', fg: '#92400E' }, // amber
  { bg: '#FBCFE8', fg: '#9D174D' }, // pink
  { bg: '#BFDBFE', fg: '#1E40AF' }, // blue
  { bg: '#A7F3D0', fg: '#065F46' }, // green
  { bg: '#E9D5FF', fg: '#6B21A8' }, // purple
  { bg: '#FED7AA', fg: '#9A3412' }, // orange
] as const;

// Deterministic so an app keeps its color forever (djb2 hash).
export function pastelFor(name: string): PastelPair {
  let h = 5381;
  for (let i = 0; i < name.length; i++) {
    h = ((h << 5) + h + name.charCodeAt(i)) | 0;
  }
  return pastels[Math.abs(h) % pastels.length];
}

export const spacing = { xs: 4, sm: 8, md: 12, lg: 16, xl: 20, xxl: 24, xxxl: 32 } as const;

export const radii = { chip: 12, button: 14, card: 18, modal: 24, fab: 18 } as const;

export const type = {
  display: { fontSize: 28, fontWeight: '800', letterSpacing: -0.5 },
  title:   { fontSize: 22, fontWeight: '800', letterSpacing: -0.5 },
  heading: { fontSize: 17, fontWeight: '700' },
  body:    { fontSize: 15, fontWeight: '400' },
  caption: { fontSize: 13, fontWeight: '600' },
  micro:   { fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase' },
} as const satisfies Record<string, TextStyle>;

// The "clay" signature: hard offset shadow, zero blur.
// Spread into styles as: { shadowColor: clay.card.color, shadowOffset: { width: 0, height: clay.card.offsetY }, shadowOpacity: 1, shadowRadius: 0, elevation: 2 }
export const clay = {
  card:    { color: colors.border,       offsetY: 3 },
  primary: { color: colors.primaryDepth, offsetY: 4 },
  danger:  { color: colors.dangerDepth,  offsetY: 3 },
} as const;

// Helper so every component builds clay shadows the same way.
export function clayShadow(kind: keyof typeof clay, pressed = false) {
  const { color, offsetY } = clay[kind];
  return {
    shadowColor: color,
    shadowOffset: { width: 0, height: pressed ? Math.max(offsetY - 2, 1) : offsetY },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: pressed ? 1 : 2,
  } as const;
}

// Press feedback: 2px squish, paired with clayShadow(kind, true).
export const pressSquish = { transform: [{ translateY: 2 }] } as const;
